
import { defineComponent, onBeforeMount, reactive } from 'vue'

// module components
import ZonesTabs from '@/modules/Standard2025TariffModule/components/ZonesTabs/index.vue'
import ZonesGrid from '@/modules/Standard2025TariffModule/components/ZonesGrid/index.vue'
import ZonesModal from '@/modules/Standard2025TariffModule/components/ZonesModal/index.vue'
import StandardParamsGrid from '@/modules/Standard2025TariffModule/components/StandardParamsGrid/index.vue'

// composable
import useNotifications from '@/common/composable/useNotifications'

// types
import type { Standard2025TariffsTariff, Standard2025TariffsMappedTariff, Standard2025TariffsStandardParam, Standard2025TariffsMappedStandardParam } from '@/core/types/billing.types'

// realisations
import { billing } from '@/core/realisations/billing.realisation'

const AUTO_TARIFF_TYPE_ID = 1
const AVIA_TARIFF_TYPE_ID = 2

export default defineComponent({
    components: {
        ZonesTabs,
        ZonesGrid,
        ZonesModal,
        StandardParamsGrid
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            //
            selected_zone_id: 1,
            auto_tariffs: [] as Array<Standard2025TariffsMappedTariff>,
            avia_tariffs: [] as Array<Standard2025TariffsMappedTariff>,
            standard_params: [] as Array<Standard2025TariffsMappedStandardParam>,
            //
            is_modal_opened: false,
        })

        const findTariffDopPrice = (tariffs: Array<Standard2025TariffsTariff>, target: Standard2025TariffsTariff): number | null => {
            let dopPrice = null

            const findedTariff = tariffs.find((tariff) => tariff.zoneId === target.zoneId && tariff.typeId === target.typeId && tariff.weight === target.weight && !tariff.isMinPrice)

            if (findedTariff) {
                dopPrice = findedTariff.price
            }

            return dopPrice
        }

        const findStandardParamAviaAmount = (standardParams: Array<Standard2025TariffsStandardParam>, target: Standard2025TariffsStandardParam): number | null => {
            let aviaAmount = null

            const findedStandardParam = standardParams.find((standardParam) => standardParam.name === target.name && standardParam.type_id === AVIA_TARIFF_TYPE_ID && standardParam.unit_id === target.unit_id)

            if (findedStandardParam) {
                aviaAmount = findedStandardParam.amount
            }

            return aviaAmount
        }

        const mapTariffs = (tariffs: Array<Standard2025TariffsTariff>): Array<Standard2025TariffsMappedTariff> => {
            return tariffs.filter((tariff) => tariff.isMinPrice).map((tariff) => {
                return {
                    fixedPrice: tariff.price,
                    dopPrice: findTariffDopPrice(tariffs, tariff),
                    typeId: tariff.typeId,
                    weight: tariff.weight,
                    zoneId: tariff.zoneId
                }
            })
        }

        const mapStandardParams = (standardParams: Array<Standard2025TariffsStandardParam>): Array<Standard2025TariffsMappedStandardParam> => {
            return standardParams.filter((standardParam) => standardParam.type_id === AUTO_TARIFF_TYPE_ID).map((standardParam) => {
                return {
                    name: standardParam.name,
                    auto_amount: standardParam.amount,
                    avia_amount: findStandardParamAviaAmount(standardParams, standardParam),
                    description: standardParam.description,
                    type_id: standardParam.type_id,
                    unit: standardParam.unit,
                    unit_id: standardParam.unit_id
                }
            })
        }

        const loadZone = async () => {
            state.auto_tariffs = []
            state.avia_tariffs = []
            state.standard_params = []

            try {
                state.is_loading = true

                const response = await billing.getStandard2025Tariffs(state.selected_zone_id)

                state.auto_tariffs = mapTariffs(response.data.tariffs.filter(tariff => tariff.typeId === AUTO_TARIFF_TYPE_ID))
                state.avia_tariffs = mapTariffs(response.data.tariffs.filter(tariff => tariff.typeId === AVIA_TARIFF_TYPE_ID))
                state.standard_params = mapStandardParams(response.data.standardParams)

                state.is_failed = false
            } catch (error) {
                console.error(error)

                state.is_failed = true
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadZone)

        const onChangeZone = (zone_id: number) => {
            state.selected_zone_id = zone_id
            loadZone()
        }

        const onOpenModal = () => {
            state.is_modal_opened = true
        }

        const onCloseModal = () => {
            state.is_modal_opened = false
        }

        return {
            state,
            onChangeZone,
            loadZone,
            onOpenModal,
            onCloseModal,
        }
    }
})
